import React, { useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import Header from '../components/Header';
import Footer from '../components/Footer';

const sectionVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: (delay = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, delay, ease: 'easeOut', type: 'spring', stiffness: 90 },
  }),
};

const cardVariants = {
  hidden: { opacity: 0, y: 40, scale: 0.9 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.6, delay: i * 0.15, ease: 'easeOut', type: 'spring', stiffness: 120 },
  }),
};

const buttonHoverVariants = {
  rest: {
    scale: 1,
    y: 0,
    transition: { duration: 0.2, ease: 'easeOut' },
  },
  hover: {
    scale: 1.05,
    y: -3,
    backgroundColor: '#1e7ca1',
    boxShadow: '0 6px 12px rgba(37, 150, 190, 0.3)',
    transition: { duration: 0.2, ease: 'easeOut' },
  },
};

const AboutUs = () => {
  const location = useLocation();
  const heroRef = useRef(null);
  const missionVisionRef = useRef(null);
  const whyExistRef = useRef(null);
  const messagesRef = useRef(null);

  const heroInView = useInView(heroRef, { margin: '-150px' });
  const missionVisionInView = useInView(missionVisionRef, { margin: '-150px' });
  const whyExistInView = useInView(whyExistRef, { margin: '-150px' });
  const messagesInView = useInView(messagesRef, { margin: '-150px' });

  const buttonClass = 'bg-[#2596be] text-white font-bold py-3 px-8 rounded-lg glow-button';

  // Scroll to top on route change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return (
    <div className="flex flex-col font-sans text-black bg-gray-100 min-h-screen">
      <style>
        {`
          html, body { 
            background-color: #f3f4f6; 
            margin: 0; 
            padding: 0; 
            min-height: 100vh;
          }
          .glow-button {
            animation: glow 1.5s ease-in-out infinite alternate;
          }
          @keyframes glow {
            from {
              box-shadow: 0 0 5px rgba(37, 150, 190, 0.4), 0 0 10px rgba(37, 150, 190, 0.3);
            }
            to {
              box-shadow: 0 0 15px rgba(37, 150, 190, 0.8), 0 0 25px rgba(37, 150, 190, 0.5);
            }
          }
        `}
      </style>

      <Header />
      {/* Mission & Vision */}
      <section className="w-full bg-gray-100 mt-12">
        <div ref={missionVisionRef} className="max-w-6xl mx-auto px-6 py-16">
          <motion.h2
            className="text-3xl font-bold text-[#2596be] mb-8 text-center"
            initial="hidden"
            animate={missionVisionInView ? 'visible' : 'hidden'}
            variants={sectionVariants}
            custom={0}
          >
            Our Mission & Vision
          </motion.h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <motion.div
              className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg"
              initial="hidden"
              animate={missionVisionInView ? 'visible' : 'hidden'}
              variants={sectionVariants}
              custom={0.2}
            >
              <h3 className="text-2xl font-bold text-[#2596be] mb-4">Our Mission</h3>
              <p className="text-base text-gray-900">
                We help businesses integrate the power of artificial intelligence into their operations so they can run more efficiently, make smarter decisions, and unlock new opportunities.
              </p>
            </motion.div>
            <motion.div
              className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg"
              initial="hidden"
              animate={missionVisionInView ? 'visible' : 'hidden'}
              variants={sectionVariants}
              custom={0.4}
            >
              <h3 className="text-2xl font-bold text-[#2596be] mb-4">Our Vision</h3>
              <p className="text-base text-gray-900">
                We want to transform how businesses operate by combining them with innovative AI solutions. Our goal is to become the go-to source for practical, reliable, and forward-thinking AI applications that help organizations of all sizes.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Why We Exist */}
      <section className="w-full bg-gray-100">
        <div ref={whyExistRef} className="max-w-6xl mx-auto px-6 py-16">
          <motion.h2
            className="text-3xl font-bold text-[#2596be] mb-8 text-center"
            initial="hidden"
            animate={whyExistInView ? 'visible' : 'hidden'}
            variants={sectionVariants}
            custom={0}
          >
            Why We Exist
          </motion.h2>
          <motion.div
            className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg text-center"
            initial="hidden"
            animate={whyExistInView ? 'visible' : 'hidden'}
            variants={sectionVariants}
            custom={0.2}
          >
            <ul className="list-none space-y-2 mb-4 ">
              <li>
                <span className=" text-base text-gray-900">Because AI adoption is still too hard.</span>
              </li>
              <li>
                <span className=" text-base text-gray-900">Because businesses don't have time to keep up with the pace of change.</span>
              </li>
              <li>
                <span className=" text-base text-gray-900">Because you shouldn't need a PhD to improve your business with AI.</span>
              </li>
            </ul>
            <p className="font-bold text-[#2596be]">We make it simple. We make it real.</p>
          </motion.div>
        </div>
      </section>

      {/* Messages */}
      <section className="w-full bg-white">
        <div ref={messagesRef} className="max-w-6xl mx-auto px-6 py-16">
          <motion.h2
            className="text-3xl font-bold text-[#2596be] mb-6 text-center"
            initial="hidden"
            animate={messagesInView ? 'visible' : 'hidden'}
            variants={sectionVariants}
            custom={0}
          >
            Our Messages
          </motion.h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              'From Insights to Action',
              'Hire AI. Transform Business.',
              "We keep up with AI—so you don't have to."
            ].map((message, index) => (
              <motion.div
                key={index}
                className="bg-gray-50 p-6 rounded-lg shadow-md hover:shadow-lg text-center"
                initial="hidden"
                animate={messagesInView ? 'visible' : 'hidden'}
                variants={cardVariants}
                custom={index}
              >
                <p className="text-lg font-semibold text-gray-900">"{message}"</p>
              </motion.div>
            ))}
          </div>
          <motion.div
            className="flex justify-center mt-8"
            initial="hidden"
            animate={messagesInView ? 'visible' : 'hidden'}
            variants={sectionVariants}
            custom={0.6}
          >
            <motion.div
              initial="rest"
              whileHover="hover"
              animate="rest"
              variants={buttonHoverVariants}
            >
              <a
                href="https://mail.google.com/mail/?view=cm&to=contact@theaiunion.com"
                target="_blank"
                rel="noopener noreferrer"
                className={buttonClass}
              >
                Contact Us
              </a>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="w-full bg-gray-100 mt-auto">
        <Footer />
      </footer>
    </div>
  );
};

export default AboutUs;